/**
 * v2 API client — group-scoped routes (/api/groups/{groupId}/*).
 * /signin bootstrap is called directly from UserSessionContext.
 */

import { authenticatedFetch } from './authService';
import { getApiUrl } from '../utils/config';

const ensureGroupId = (groupId) => {
  if (!groupId || typeof groupId !== 'string') {
    throw new Error('api call requires a groupId from GroupContext');
  }
};

// Reject anything that could normalize outside /groups/{groupId}/* scope
// (e.g. "/../signin") or hop to another origin ("//evil.com", "http://...").
const ensureScopedPath = (path) => {
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) {
    throw new Error('api path must start with "/" and stay within group scope');
  }
  if (path.includes('..') || path.includes('\\')) {
    throw new Error('api path may not contain ".." or backslashes');
  }
};

const request = async (method, groupId, path, body) => {
  ensureGroupId(groupId);
  if (path !== '') ensureScopedPath(path);
  const base = getApiUrl();
  const url = `${base}/groups/${encodeURIComponent(groupId)}${path}`;
  const res = await authenticatedFetch(url, {
    method,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {})
  });
  let data = null;
  const text = await res.text();
  if (text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }
  if (!res.ok) {
    const err = new Error((data && data.error) || `${method} ${path} failed: ${res.status}`);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
};

export const api = {
  get: (groupId, path) => request('GET', groupId, path),
  post: (groupId, path, body) => request('POST', groupId, path, body),
  put: (groupId, path, body) => request('PUT', groupId, path, body),
  delete: (groupId, path) => request('DELETE', groupId, path)
};

const requestGlobal = async (method, path, body) => {
  ensureScopedPath(path);
  const base = getApiUrl();
  const url = `${base}${path}`;
  const res = await authenticatedFetch(url, {
    method,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {})
  });
  let data = null;
  const text = await res.text();
  if (text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }
  if (!res.ok) {
    const err = new Error((data && data.error) || `${method} ${path} failed: ${res.status}`);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
};

export const apiGlobal = {
  get: (path) => requestGlobal('GET', path),
  post: (path, body) => requestGlobal('POST', path, body),
  put: (path, body) => requestGlobal('PUT', path, body),
  // body lets callers send the account-delete OTP code (#1165) on DELETE.
  delete: (path, body) => requestGlobal('DELETE', path, body)
};

export default api;
